import moment from "moment-timezone";

/**
 * The ONE place the end-user app learns which time zone the station is in.
 *
 * Customer-facing times and calendar days are the station's wall clock
 * (ADR-0019 rule 2) — never the phone's: a customer abroad, or a phone set to
 * another zone, books and sees the same 22.10. and the same 18:00 as a phone in
 * Germany.
 *
 * Each location carries its own zone (#3219, `timeZone` on every location the
 * API returns; ADR-0019 amendment 2026-10-13). Every helper below takes it as
 * a trailing `timeZone` and defaults to `STATION_TIME_ZONE`, the zone of every
 * location that has none (and of every location created before the column) —
 * so a Berlin location computes exactly what it did before. A location's zone
 * is read in ONE place, `zoneOfLocation`; the selected booking location's
 * through `useSelectedLocationTimeZone` (util/locationTimeZone.ts).
 *
 * Nothing else in src/ writes the literal — `scripts/time-guard` fails if it
 * does.
 *
 * Mirrors: backend/src/core/lib/date.ts `BOOKING_TIME_ZONE`,
 * new-backend/{admin,app}/src/lib/stationTime.ts,
 * frontend/admin-ui/src/utils/stationTime.ts.
 */
export const STATION_TIME_ZONE = "Europe/Berlin";

/**
 * The zone of a location (#3219): its `timeZone`, or `STATION_TIME_ZONE` when
 * it has none (no location yet, or a payload that does not carry the field).
 *
 * A zone this app's tz data does not know THROWS rather than being read
 * silently: moment would compute it as UTC, and the app would offer days,
 * slots and nights the server — which validates in the location's real zone —
 * then refuses. The admin write path only stores zones `Intl` resolves, so this
 * fires only on a hand edit or a tz-data gap. Checked once per distinct name.
 *
 * Mirrors: backend/src/core/lib/date.ts `zoneOf`,
 * new-backend/{admin,app}/src/lib/stationTime.ts `zoneOfLocation`,
 * frontend/admin-ui/src/utils/stationTime.ts `zoneOfLocation`.
 */
const checkedZones = new Set<string>([STATION_TIME_ZONE]);
export const zoneOfLocation = (
  location?: { timeZone?: string | null } | null
): string => {
  const zone = location?.timeZone || STATION_TIME_ZONE;
  if (!checkedZones.has(zone)) {
    if (!moment.tz.zone(zone)) {
      throw new Error(`location timeZone "${zone}" is not a known IANA time zone`);
    }
    checkedZones.add(zone);
  }
  return zone;
};

/** An instant read on the station's clock. */
export const atStation = (
  instant: moment.MomentInput,
  timeZone: string = STATION_TIME_ZONE
): moment.Moment => moment.tz(instant, timeZone);

/**
 * The calendar day the customer picked, as a "YYYY-MM-DD" label. The date
 * picker hands back midnight in the PHONE's zone; `format` reads the label in
 * the moment's own zone, i.e. the day actually clicked — never `.tz(...)` it
 * first, which turns a Tokyo phone's 22.10. into the Berlin 21.10.
 */
export const pickedDayLabel = (pickedDay: moment.Moment): string =>
  pickedDay.format("YYYY-MM-DD");

/** The station's calendar day an instant falls on, as a "YYYY-MM-DD" label. */
export const stationDayLabel = (
  instant: moment.MomentInput,
  timeZone: string = STATION_TIME_ZONE
): string => atStation(instant, timeZone).format("YYYY-MM-DD");

/**
 * The day the customer picked, as midnight AT THE STATION — the station day
 * with that label, whatever zone the phone is in.
 */
export const stationDay = (
  pickedDay: moment.Moment,
  timeZone: string = STATION_TIME_ZONE
): moment.Moment => moment.tz(pickedDayLabel(pickedDay), "YYYY-MM-DD", timeZone);

/**
 * Midnight AT THE STATION of the calendar day the customer picked — what a
 * room-family check-in/check-out date is sent as. See #3215.
 */
export const stationDayStart = (
  pickedDay: moment.Moment,
  timeZone: string = STATION_TIME_ZONE
): string => stationDay(pickedDay, timeZone).toISOString();

/**
 * The instant a station slot starts: the day the customer picked (its LABEL)
 * at the slot's station wall-clock time ("HH:mm" / "H:mm"). Station slot lists
 * are keyed in station time, so a slot is never re-read on the phone's clock.
 */
export const stationSlotStart = (
  pickedDay: moment.Moment,
  time: string,
  timeZone: string = STATION_TIME_ZONE
): moment.Moment =>
  moment.tz(`${pickedDayLabel(pickedDay)} ${time}`, "YYYY-MM-DD HH:mm", timeZone);

/**
 * The value a DATE PICKER shows for a station-zoned moment: local midnight of
 * its STATION calendar day. Handing the picker the instant itself
 * (`moment.toDate()`) renders it on the phone's clock — a Berlin 08:00 start
 * is the previous day on a Los Angeles phone.
 */
export const stationDayForPicker = (
  stationMoment: moment.Moment,
  timeZone: string = STATION_TIME_ZONE
): Date => {
  const day = atStation(stationMoment, timeZone);
  return new Date(day.year(), day.month(), day.date());
};
